import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { TeamMember } from '../data/teamMembers';
import Modal from './common/Modal';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Card = styled.div<{ delay?: number }>`
  background: #ffffff;
  border-radius: 20px;
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  border: 1px solid rgba(198, 167, 94, 0.1);
  box-shadow: 0 4px 20px rgba(26, 26, 26, 0.06);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeInUp} 0.7s ease-out both;
  animation-delay: ${props => props.delay ?? 0}s;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--brand-gold), rgba(198, 167, 94, 0.3));
    opacity: 0;
    transition: opacity 0.35s ease;
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 40px rgba(26, 26, 26, 0.12), 0 4px 12px rgba(198, 167, 94, 0.08);
    border-color: rgba(198, 167, 94, 0.2);
  }

  &:hover::before {
    opacity: 1;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 16px;

    &:hover {
      transform: translateY(-3px);
    }
  }
`;

const AvatarWrapper = styled.div`
  margin-bottom: 1.5rem;
`;

const HeadshotImageWrapper = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 0 0 3px rgba(198, 167, 94, 0.2);
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 50% 20%;
    display: block;
  }
`;

const AILogoAvatar = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15), 0 0 0 3px rgba(198, 167, 94, 0.35);
  overflow: hidden;

  img {
    width: 80px;
    height: 80px;
    object-fit: contain;
  }
`;

const CofounderBadge = styled.span`
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: lowercase;
  color: var(--brand-gold);
  background: rgba(198, 167, 94, 0.1);
  border: 1px solid rgba(198, 167, 94, 0.3);
  border-radius: 20px;
  padding: 0.2rem 0.65rem;
  margin-bottom: 0.5rem;
`;

const InitialsPlaceholder = styled.div<{ initials: string }>`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--brand-black) 0%, #3a3a3a 50%, rgba(198, 167, 94, 0.8) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  font-weight: 700;
  color: white;
  letter-spacing: 0.05em;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15), 0 0 0 3px rgba(198, 167, 94, 0.2);

  &::after {
    content: '${props => props.initials}';
  }
`;

const MemberName = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--brand-black);
  margin-bottom: 0.35rem;
  letter-spacing: -0.01em;
`;

const MemberRole = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(198, 167, 94, 0.9);
  text-transform: lowercase;
  letter-spacing: 0.03em;
  margin-bottom: 1.1rem;
`;

const MemberBio = styled.p`
  font-size: 0.95rem;
  line-height: 1.65;
  color: var(--brand-text-secondary);
  flex: 1;
  margin-bottom: 1.25rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const LinkedInLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--brand-text-secondary);
  text-decoration: none;
  padding: 0.45rem 1rem;
  border-radius: 20px;
  border: 1px solid rgba(198, 167, 94, 0.25);
  transition: all 0.25s ease;

  svg {
    width: 16px;
    height: 16px;
    opacity: 0.75;
    transition: opacity 0.2s ease;
  }

  &:hover {
    color: var(--brand-black);
    background: rgba(198, 167, 94, 0.08);
    border-color: rgba(198, 167, 94, 0.5);

    svg {
      opacity: 1;
    }
  }
`;

const AchievementsBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--brand-black);
  background: rgba(198, 167, 94, 0.15);
  padding: 0.5rem 1.2rem;
  border-radius: 20px;
  border: 1px solid rgba(198, 167, 94, 0.3);
  transition: all 0.25s ease;
  cursor: pointer;

  &:hover {
    background: rgba(198, 167, 94, 0.25);
    border-color: rgba(198, 167, 94, 0.8);
    transform: translateY(-2px);
  }
`;

const AchievementsList = styled.ul`
  list-style: none;
  padding: 1rem 0.5rem;
  margin: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  
  li {
    position: relative;
    padding-left: 2.2rem;
    color: #333;
    font-size: 1.05rem;
    line-height: 1.6;
    letter-spacing: 0.01em;
    
    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      top: 0.1rem;
      color: var(--brand-gold);
      font-size: 1.1rem;
    }
  }
`;

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

interface Props {
  member: TeamMember;
  delay?: number;
}

const TeamMemberCard: React.FC<Props> = ({ member, delay }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
    <Card delay={delay}>
      <AvatarWrapper>
        {member.isAI && member.imagePath ? (
          <AILogoAvatar>
            <img src={member.imagePath} alt="Prest0n" onError={() => setImgFailed(true)} />
          </AILogoAvatar>
        ) : member.imagePath && !imgFailed ? (
          <HeadshotImageWrapper>
            <img src={member.imagePath} alt={member.name} onError={() => setImgFailed(true)} />
          </HeadshotImageWrapper>
        ) : (
          <InitialsPlaceholder initials={getInitials(member.name)} />
        )}
      </AvatarWrapper>
      {member.isCofounder && <CofounderBadge>co-founder</CofounderBadge>}
      <MemberName>{member.name}</MemberName>
      {member.role && <MemberRole>{member.role}</MemberRole>}
      <MemberBio>{member.bio}</MemberBio>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        {member.internalAchievements?.length ? (
          <AchievementsBtn onClick={() => setIsModalOpen(true)}>
            view achievements
          </AchievementsBtn>
        ) : null}
        
        {member.linkedinUrl && (
          <LinkedInLink href={member.linkedinUrl} target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
              <rect x="2" y="9" width="4" height="12"/>
              <circle cx="4" cy="4" r="2"/>
            </svg>
            linkedin
          </LinkedInLink>
        )}
      </div>
    </Card>

    {member.internalAchievements && (
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0', fontFamily: 'Space Grotesk, sans-serif' }}>
            Prest
            <img src="/images/project-olivia-logo.webp?v=20260715" alt="0" style={{ width: '1em', height: '1em', objectFit: 'contain', display: 'inline-block', verticalAlign: 'baseline', margin: '0 0.5px' }} />
            n — Output Feed
          </span>
        }
        size="large"
        footer={null}
      >
        <div style={{ paddingBottom: '1rem', borderBottom: '1px solid rgba(0,0,0,0.05)', marginBottom: '1rem', fontSize: '1.05rem', color: '#666', lineHeight: 1.6 }}>
          A running log of infrastructure, product, and code shipped autonomously for the prest
          <img src="/images/project-olivia-logo.webp?v=20260715" alt="0" style={{ width: '1em', height: '1em', objectFit: 'contain', display: 'inline-block', verticalAlign: 'baseline', margin: '0 1px' }} />
          {' '}platform:
        </div>
        <AchievementsList>
          {member.internalAchievements.map((achievement, idx) => (
            <li key={idx}>{achievement}</li>
          ))}
        </AchievementsList>
      </Modal>
    )}
    </>
  );
};

export default TeamMemberCard;
